import React, { useState, useEffect } from 'react';
import PasswordInput from '../FormInputs/PasswordInput/PasswordInput';
import Button from '../Button/Button';
import { useToasts } from '../../hooks/useToasts';
import { submitNewPassword } from '../../utils/api/passwordReset';
import { login } from '../../utils/api/login';
import { navigateToDashboard } from '../../utils/navigation';

const SetNewPasswordForm = () => {
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmationError, setConfirmationError] = useState('');
  const { showToast } = useToasts();

  const { location } = window;
  const fixedSearch = location.search.replace(/&amp;/g, '&');
  const searchParams = new URLSearchParams(fixedSearch);
  const token = searchParams.get('reset_password_token');
  const confirmationToken = searchParams.get('confirmation_token');
  // Invitation flag is forwarded on submit so backend uses invitation token TTL.
  const isInvitation = searchParams.get('invitation') === 'true';
  const [dynamicNewPassError, setDynamicNewPassError] = useState('');
  const dynamicConfirmationNewPassError = passwordConfirmation && password !== passwordConfirmation && !confirmationError
    ? 'Password does not match'
    : confirmationError;

  useEffect(() => {
    showToast({
      message: 'Please set password to confirm your account and log in.',
      type: 'info',
    });
  }, [showToast]);

  useEffect(() => {
    if (passwordError) {
      setDynamicNewPassError(passwordError);
    } else if (password && password.length > 0) {
      setDynamicNewPassError('Please add all necessary criteria to create a safe password');
    } else {
      setDynamicNewPassError('');
    }
  }, [passwordError, password]);

  const handleSubmit = async () => {
    setPasswordError('');
    setConfirmationError('');

    if (!password) {
      setPasswordError('Password is required');
    }

    if (!passwordConfirmation) {
      setConfirmationError('Confirmation is required');
    }

    if (!password || !passwordConfirmation) return;

    if (password !== passwordConfirmation) {
      const message = 'Passwords do not match';
      setConfirmationError(message);
      showToast({ message, type: 'error' });
      return;
    }

    const result = await submitNewPassword({
      password,
      passwordConfirmation,
      token,
      confirmationToken,
      invitation: isInvitation,
    });

    if (result.success) {
      const loginResult = await login(result.email, password);

      if (loginResult.success) {
        showToast({ message: 'Your password has been set successfully.', type: 'success' });
        setTimeout(() => {
          navigateToDashboard();
        }, 3000);
      } else {
        showToast({ message: loginResult.error, type: 'error' });
      }
    } else {
      setPasswordError(result.error);
      setConfirmationError(result.error);
      showToast({ message: result.error, type: 'error' });
    }
  };

  return (
    <div className="w-77 min-h-83.5">
      <h3 className="text-2xl font-bold text-center mb-6">Set password</h3>

      <div className="mb-5.5">
        <PasswordInput
          label="New password"
          name="password"
          dataTestId="set-password-new-input"
          value={password}
          setValue={setPassword}
          placeholder="password"
          isRequired
          isNewPassword
          isValid={!dynamicNewPassError}
          errorMessage={dynamicNewPassError}
          fontSize="text-sm"
          labelMargin="mb-1"
        />
      </div>

      <div className="mb-6.5">
        <PasswordInput
          label="Confirm new password"
          name="passwordConfirmation"
          dataTestId="set-password-confirm-input"
          value={passwordConfirmation}
          setValue={setPasswordConfirmation}
          placeholder="password"
          isRequired
          isNewPassword={false}
          isValid={!!password && !!passwordConfirmation && password === passwordConfirmation}
          errorMessage={dynamicConfirmationNewPassError}
          fontSize="text-sm"
          labelMargin="mb-1"
        />
      </div>

      <div className="flex justify-center items-center">
        <Button
          label="Set password"
          type="button"
          dataTestId="set-password-submit-button"
          onClick={handleSubmit}
          className="inline-flex h-9.5 min-w-20.5 justify-center items-center flex-shrink-0"
          fontWeight="font-medium"
        />
      </div>
    </div>
  );
};

export default SetNewPasswordForm;
